import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import config from "../Config";
import Topbar from "../Components/Topbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageSelectDialog from "../Components/ImageSelectDialog";
import { dollarsStringToCents } from "../Utils/money";

function Service() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [showImageSelectDialog, setShowImageSelectDialog] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);

  const [formData, setFormData] = useState({
    sub_category_id: "",
    title: "",
    amount: "",
    currency: "CAD",
    eta: "",
    subsequent_price: "",
    subtitle: "",
    tab_title: "",
    tab_subtitle: "",
    description: "",
    image: "",
    images: [],
    videos: [],
    label: "",
    available: true,
    allows_insurance: false,
    service_type: "HOUR",
    valid_extra_charge: ["CANCEL", "RESEDULE", "OTHER"],
    terms_and_conditions: [],
    faq: [],
    bottom_note: "",
    pricing_text: "",
    priority: 999,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchSubCategories();
    fetchCategories();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/service?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setServices(data);
      } else {
        throw new Error("Failed to fetch services");
      }
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/subcategories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubCategories(data);
      } else {
        throw new Error("Failed to fetch subcategories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/categories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        throw new Error("Failed to fetch categories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleShowCategory = (categoryId) => {
    const category = categories.find((cat) => cat.category_id === categoryId);
    if (category) {
      setSelectedCategory(category);
      setShowCategoryModal(true);
    }
  };

  const handleShowSubCategory = (subCategoryId) => {
    const subCategory = subCategories.find(
      (sub) => sub.sub_category_id === subCategoryId
    );
    if (subCategory) {
      setSelectedSubCategory(subCategory);
      setShowSubCategoryModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);

        const uploadResponse = await fetch(
          "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
          {
            method: "POST",
            headers: {
              "X-Client": "ADMIN",
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData[0];
          toast.success("Image uploaded successfully!");
        } else {
          toast.error("Failed to upload image");
          throw new Error("Failed to upload image");
        }
      }

      const url = isEditing
        ? `${config.apiUrl}/api/v1/admin/service/${editingId}`
        : `${config.apiUrl}/api/v1/admin/service`;

      const method = isEditing ? "PUT" : "POST";

      const amountCents = dollarsStringToCents(formData.amount);
      const subsequentCents = dollarsStringToCents(formData.subsequent_price);
      if (Number.isNaN(amountCents) || amountCents < 0) {
        toast.error("Enter a valid amount in dollars");
        throw new Error("Invalid amount");
      }
      if (Number.isNaN(subsequentCents) || subsequentCents < 0) {
        toast.error("Enter a valid subsequent price in dollars");
        throw new Error("Invalid subsequent price");
      }

      const working = {
        ...formData,
        image: imageUrl,
        amount: amountCents,
        subsequent_price: subsequentCents,
      };

      Object.keys(working).forEach((key) => {
        if (typeof working[key] === "string" && working[key].trim() === "") {
          working[key] = null;
        }
      });

      const defaultCharges = ["CANCEL", "RESEDULE", "OTHER"];
      const validExtraCharge = [...working.valid_extra_charge];

      defaultCharges.forEach((charge) => {
        if (!validExtraCharge.includes(charge)) {
          validExtraCharge.push(charge);
        }
      });

      const priority =
        working.priority !== null && working.priority !== undefined && working.priority !== ""
          ? working.priority
          : 999;

      const dataToSend = {
        ...working,
        valid_extra_charge: validExtraCharge,
        priority: priority,
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        toast.success(
          `Service ${isEditing ? "updated" : "created"} successfully`
        );
        fetchServices();
        resetForm();
        setShowModal(false);
      } else {
        throw new Error(`Failed to ${isEditing ? "update" : "create"} service`);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (service) => {
    navigate(`/services/${service.service_id}`);
  };

  const handleShowDetails = (service) => {
    navigate(`/services/view/${service.service_id}`);
  };

  const resetForm = () => {
    setFormData({
      sub_category_id: "",
      title: "",
      amount: "",
      currency: "CAD",
      eta: "",
      subsequent_price: "",
      subtitle: "",
      tab_title: "",
      tab_subtitle: "",
      description: "",
      image: "",
      images: [],
      videos: [],
      label: "",
      available: true,
      allows_insurance: false,
      service_type: "HOUR",
      valid_extra_charge: ["CANCEL", "RESEDULE", "OTHER"],
      terms_and_conditions: [],
      faq: [],
      bottom_note: "",
      pricing_text: "",
      priority: 999,
    });
    setImageFile(null);
    setImagePreview("");
    setIsEditing(false);
    setEditingId(null);
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAdditionalImageUpload = async (file, index) => {
    const token = localStorage.getItem("token");
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      const uploadResponse = await fetch(
        "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
        {
          method: "POST",
          headers: {
            "X-Client": "ADMIN",
            Authorization: `Bearer ${token}`,
          },
          body: uploadFormData,
        }
      );

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        const imageUrl = uploadData[0];
        
        const newImages = [...formData.images];
        newImages[index] = imageUrl;
        setFormData({ ...formData, images: newImages });
        
        toast.success("Image uploaded successfully!");
      } else {
        toast.error("Failed to upload image");
        throw new Error("Failed to upload image");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Services</h1>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Add Service
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-100 border-l-4 border-red-500 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subcategory
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {services.map((service) => {
                  const subCategory = subCategories.find(
                    (sub) => sub.sub_category_id === service.sub_category_id
                  );
                  const category = categories.find(
                    (cat) => cat.category_id === subCategory?.category_id
                  );

                  return (
                    <tr key={service.service_id} className="hover:bg-gray-50">
                      <td
                        className="text-left px-6 py-4 text-indigo-600 hover:text-indigo-900 cursor-pointer"
                        onClick={() =>
                          handleShowCategory(category?.category_id)
                        }
                      >
                        {category?.title || "-"}
                      </td>
                      <td
                        className="text-left px-6 py-4 text-indigo-600 hover:text-indigo-900 cursor-pointer"
                        onClick={() =>
                          handleShowSubCategory(subCategory?.sub_category_id)
                        }
                      >
                        {subCategory?.title || "-"}
                      </td>
                      <td
                        className="text-left px-6 py-4 text-indigo-600 hover:text-indigo-900 cursor-pointer"
                        onClick={() => handleShowDetails(service)}
                      >
                        {service.title}
                      </td>
                      <td className="text-left px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            service.available
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {service.available ? "Available" : "Unavailable"}
                        </span>
                      </td>
                      <td className="text-left px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/services/${service.service_id}/content-sections`);
                          }}
                          className="text-teal-600 hover:text-teal-900 mr-4"
                        >
                          Content
                        </button>
                        <button
                          onClick={() => handleEdit(service)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {/* <button
                          onClick={() => handleDelete(service.service_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button> */}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showCategoryModal && selectedCategory && (
          <div className="fixed inset-0 z-[120] bg-gray-500 bg-opacity-75 flex items-center justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-lg p-6 sm:p-8 max-w-2xl w-full mx-auto my-8 max-h-[min(90dvh,100%)] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Category Details</h2>
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setSelectedCategory(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              {selectedCategory.image && (
                <div className="mb-6">
                  <img
                    src={selectedCategory.image}
                    alt={selectedCategory.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}
              <table className="w-full">
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(selectedCategory).map(
                    ([key, value]) =>
                      key !== "image" && (
                        <tr key={key}>
                          <td className="text-left font-black py-3 text-sm font-medium text-gray-500">
                            {key.charAt(0).toUpperCase() +
                              key.slice(1).replace(/_/g, " ")}
                          </td>
                          <td className="text-left py-3 text-sm text-gray-900">
                            {typeof value === "boolean" ? (
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  value
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {value ? "Yes" : "No"}
                              </span>
                            ) : typeof value === "object" ? (
                              value === null ? (
                                "-"
                              ) : (
                                value.toString()
                              )
                            ) : (
                              String(value || "-")
                            )}
                          </td>
                        </tr>
                      )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showSubCategoryModal && selectedSubCategory && (
          <div className="fixed inset-0 z-[120] bg-gray-500 bg-opacity-75 flex items-center justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-lg p-6 sm:p-8 max-w-2xl w-full mx-auto my-8 max-h-[min(90dvh,100%)] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Subcategory Details</h2>
                <button
                  onClick={() => {
                    setShowSubCategoryModal(false);
                    setSelectedSubCategory(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              {selectedSubCategory.image && (
                <div className="mb-6">
                  <img
                    src={selectedSubCategory.image}
                    alt={selectedSubCategory.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}
              <table className="w-full">
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(selectedSubCategory).map(
                    ([key, value]) =>
                      key !== "image" && (
                        <tr key={key}>
                          <td className="text-left font-black py-3 text-sm font-medium text-gray-500">
                            {key.charAt(0).toUpperCase() +
                              key.slice(1).replace(/_/g, " ")}
                          </td>
                          <td className="text-left py-3 text-sm text-gray-900">
                            {key === "category_id" ? (
                              categories.find(
                                (cat) => cat.category_id === value
                              )?.title || String(value)
                            ) : typeof value === "boolean" ? (
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  value
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {value ? "Yes" : "No"}
                              </span>
                            ) : typeof value === "object" ? (
                              value === null ? (
                                "-"
                              ) : (
                                value.toString()
                              )
                            ) : (
                              String(value || "-")
                            )}
                          </td>
                        </tr>
                      )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-[120] bg-gray-500 bg-opacity-75 flex items-center justify-center overflow-y-auto p-4 py-8 sm:py-12">
            <div className="bg-white rounded-lg p-6 sm:p-8 max-w-2xl w-full max-h-[min(90dvh,100%)] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">
                  {isEditing ? "Edit Service" : "Add New Service"}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subcategory <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.sub_category_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sub_category_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select a subcategory</option>
                    {subCategories.map((subCategory) => (
                      <option
                        key={subCategory.sub_category_id}
                        value={subCategory.sub_category_id}
                      >
                        {
                          categories.find(
                            (cat) => cat.category_id === subCategory.category_id
                          )?.title
                        }{" "}
                        - {subCategory.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Single door refrigerator check-up"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.service_type}
                    onChange={(e) =>
                      setFormData({ ...formData, service_type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="HOUR">Hour</option>
                    <option value="AUTO">Auto</option>
                    <option value="QUOTATION">Quotation</option>
                    <option value="QUANTITY">Quantity</option>
                    <option value="DIAGNOSTIC">Diagnostic</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.service_type === "HOUR" ? "Amount for first hour (dollars)" : 
                       formData.service_type === "QUOTATION" ? "Price for quotation (dollars)" :
                       "Amount for first quantity (dollars)"} <span className="text-red-500">*</span>
                       </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          amount: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency <span className="text-red-500">*</span>
                    </label>
                    <select
                      disabled
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="">Select currency</option>
                      <option value="CAD">CAD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.service_type === "QUANTITY" ? "Time for First Quantity (minutes)" : "ETA (minutes)"} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.eta}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          eta: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {["QUANTITY", "QUOTATION","DIAGNOSTIC","AUTO"].includes(formData.service_type)?"Price extra quantity (dollars)": "Subsequent price per hour (dollars)" } <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.subsequent_price}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subsequent_price: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) =>
                      setFormData({ ...formData, subtitle: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tab Title
                  </label>
                  <input
                    type="text"
                    value={formData.tab_title}
                    onChange={(e) =>
                      setFormData({ ...formData, tab_title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter tab title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tab Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.tab_subtitle}
                    onChange={(e) =>
                      setFormData({ ...formData, tab_subtitle: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter tab subtitle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo Image
                  </label>
                  {imagePreview && (
                    <div className="mb-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-md"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowImageSelectDialog(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {imagePreview ? "Edit Image" : "Upload Image"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Images
                  </label>
                  <div className="space-y-2">
                    {formData.images.map((image, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        {image && (
                          <div className="flex-shrink-0">
                            <img
                              src={image}
                              alt={`Preview ${index + 1}`}
                              className="w-16 h-16 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <input
                          type="text"
                          value={image}
                          onChange={(e) => {
                            const newImages = [...formData.images];
                            newImages[index] = e.target.value;
                            setFormData({ ...formData, images: newImages });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Enter image URL or upload image"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setUploadingImageIndex(index);
                            setShowImageSelectDialog(true);
                          }}
                          className="px-3 py-2 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-50"
                        >
                          Upload
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = formData.images.filter((_, i) => i !== index);
                            setFormData({ ...formData, images: newImages });
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          images: [...formData.images, ""]
                        });
                      }}
                      className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      Add Image
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Videos
                  </label>
                  <div className="space-y-2">
                    {formData.videos.map((video, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={video}
                          onChange={(e) => {
                            const newVideos = [...formData.videos];
                            newVideos[index] = e.target.value;
                            setFormData({ ...formData, videos: newVideos });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newVideos = formData.videos.filter((_, i) => i !== index);
                            setFormData({ ...formData, videos: newVideos });
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          videos: [...formData.videos, ""]
                        });
                      }}
                      className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      Add Video Link
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) =>
                      setFormData({ ...formData, label: e.target.value })
                    }
                    placeholder="e.g. NEW, QUICK etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value ? parseInt(e.target.value) : 999,
                      })
                    }
                    placeholder="999"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Lower number = higher priority. Default: 999
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Extra Charges
                  </label>
                  <div className="space-y-2">
                    {[
                      "MATERIAL_COST",
                      "VISIT_COST",
                      "CANCEL_COST",
                      "EXTRA_WORKER_COST",
                      "RE_VISIT_COST",
                      "EXTRA_QUANTITY",
                      "EXTRA_TIME",
                      "CANCEL",
                      "RESEDULE",
                      "OTHER"
                    ].map((charge) => (
                      <div key={charge} className="flex items-center">
                        <input
                          type="checkbox"
                          id={charge}
                          checked={["CANCEL", "RESEDULE", "OTHER"].includes(charge) ? true : formData.valid_extra_charge.includes(charge)}
                          onChange={(e) => {
                            if (["CANCEL", "RESEDULE", "OTHER"].includes(charge)) {
                              return; // Prevent changes for these three options
                            }
                            const newCharges = e.target.checked
                              ? [...formData.valid_extra_charge, charge]
                              : formData.valid_extra_charge.filter(c => c !== charge);
                            setFormData({ ...formData, valid_extra_charge: newCharges });
                          }}
                          disabled={["CANCEL", "RESEDULE", "OTHER"].includes(charge)}
                          className={`h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded ${
                            ["CANCEL", "RESEDULE", "OTHER"].includes(charge) ? "opacity-50" : ""
                          }`}
                        />
                        <label htmlFor={charge} className="ml-2 block text-sm text-gray-900">
                          {charge.replace(/_/g, ' ')}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Terms and Conditions
                  </label>
                  <div className="space-y-2">
                    {formData.terms_and_conditions.map((term, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={term}
                          onChange={(e) => {
                            const newTerms = [...formData.terms_and_conditions];
                            newTerms[index] = e.target.value;
                            setFormData({ ...formData, terms_and_conditions: newTerms });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Enter term and condition"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newTerms = formData.terms_and_conditions.filter((_, i) => i !== index);
                            setFormData({ ...formData, terms_and_conditions: newTerms });
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          terms_and_conditions: [...formData.terms_and_conditions, ""]
                        });
                      }}
                      className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      Add Term
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    FAQ
                  </label>
                  <div className="space-y-4">
                    {formData.faq.map((faqItem, index) => (
                      <div key={index} className="border border-gray-200 rounded-md p-4">
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={faqItem.question || ""}
                            onChange={(e) => {
                              const newFaq = [...formData.faq];
                              newFaq[index] = { ...newFaq[index], question: e.target.value };
                              setFormData({ ...formData, faq: newFaq });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter question"
                          />
                          <textarea
                            value={faqItem.answer || ""}
                            onChange={(e) => {
                              const newFaq = [...formData.faq];
                              newFaq[index] = { ...newFaq[index], answer: e.target.value };
                              setFormData({ ...formData, faq: newFaq });
                            }}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter answer"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newFaq = formData.faq.filter((_, i) => i !== index);
                            setFormData({ ...formData, faq: newFaq });
                          }}
                          className="mt-2 text-red-600 hover:text-red-900 text-sm"
                        >
                          Remove FAQ
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          faq: [...formData.faq, { question: "", answer: "" }]
                        });
                      }}
                      className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      Add FAQ
                    </button>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.available}
                    onChange={(e) =>
                      setFormData({ ...formData, available: e.target.checked })
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Available
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allows_insurance}
                    onChange={(e) =>
                      setFormData({ ...formData, allows_insurance: e.target.checked })
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Allows Insurance
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bottom Note
                  </label>
                  <textarea
                    value={formData.bottom_note}
                    onChange={(e) =>
                      setFormData({ ...formData, bottom_note: e.target.value })
                    }
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter bottom note (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pricing Text
                  </label>
                  <textarea
                    value={formData.pricing_text}
                    onChange={(e) =>
                      setFormData({ ...formData, pricing_text: e.target.value })
                    }
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter pricing text (optional)"
                  />
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowModal(false);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {isEditing ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
      <ImageSelectDialog
        open={showImageSelectDialog}
        onClose={() => {
          setShowImageSelectDialog(false);
          setUploadingImageIndex(null);
        }}
        onFileProcessed={(file) => {
          if (uploadingImageIndex !== null) {
            // Handle additional image upload
            handleAdditionalImageUpload(file, uploadingImageIndex);
            setUploadingImageIndex(null);
          } else {
            // Handle main logo image
            handleImageSelect(file);
          }
          setShowImageSelectDialog(false);
        }}
      />
    </div>
  );
}

export default Service;
